import PropTypes from "prop-types"
import React from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Form from "src/components/Form"
import Modal from "src/components/Modal"

import { createTaxRegistration, getMarinaTaxes } from "src/api/Manage/Taxes"

import { useToast } from "src/hooks/use_toast"

export default function AddTaxRegistrationModal({
  isOpen,
  onClose,
  existingTaxRegistrations,
}) {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      marinaTaxId: "",
      identifier: "",
    },
  })

  const { data: marinaTaxesData } = useQuery({
    queryKey: ["marina", marinaSlug, "marinaTaxes"],
    queryFn: () => getMarinaTaxes(marinaSlug),
    enabled: isOpen,
  })

  const handleClose = () => {
    onClose()
    // Reset form after modal close animation completes (300ms)
    // This prevents the form flash during the closing animation
    setTimeout(() => {
      reset()
    }, 300)
  }

  const {
    mutate: createTaxRegistrationMutation,
    isLoading: createTaxRegistrationLoading,
  } = useMutation({
    mutationKey: "createTaxRegistration",
    mutationFn: async (data) => createTaxRegistration(marinaSlug, data),
    onSuccess: ({ data: { name, identifier } }) => {
      showToast(`Tax Registration "${identifier}" for ${name} has been added`, {
        type: "success",
      })
      // Invalidate queries so the tax registrations list and available taxes dropdown update
      queryClient.invalidateQueries(["marina", marinaSlug, "taxRegistrations"])
      queryClient.invalidateQueries(["marina", marinaSlug, "marinaTaxes"])
      handleClose()
    },
    onError: (error) => {
      showToast(error.message, { type: "error" })
    },
  })

  const handleFormSubmit = (data) => {
    createTaxRegistrationMutation(data)
  }

  const usedTaxableIds = new Set(
    existingTaxRegistrations.map((taxRegistration) => taxRegistration.taxableId)
  )
  const availableTaxes =
    marinaTaxesData?.data.filter((tax) => !usedTaxableIds.has(tax.id)) || []

  return (
    <Modal onClose={handleClose} isOpen={isOpen} size="mediumFixed">
      <Modal.Header title={"Add Tax Registration"} />
      <Modal.Body>
        <Form>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <section>
              <Form.Label htmlFor="marinaTaxId" required>
                Tax
              </Form.Label>
              <Form.Select
                id="marinaTaxId"
                {...register("marinaTaxId", { required: true })}
              >
                <option value="" disabled>
                  Select Tax
                </option>
                {availableTaxes.map((tax) => (
                  <option key={tax.id} value={tax.id}>
                    {tax.name} ({tax.shortTaxTypeDisplay})
                  </option>
                ))}
              </Form.Select>
              <Form.Error>{errors.marinaTaxId && "Tax is required"}</Form.Error>
            </section>
            <section>
              <Form.Label htmlFor="identifier" required>
                Tax Registration
              </Form.Label>
              <Form.TextField
                id="identifier"
                {...register("identifier", { required: true })}
              />
              <Form.Error>
                {errors.identifier && "Tax Registration is required"}
              </Form.Error>
            </section>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer
        confirmBtnType={"submit"}
        onSubmit={handleSubmit(handleFormSubmit)}
        onClose={handleClose}
        confirmBtnLoading={createTaxRegistrationLoading}
      />
    </Modal>
  )
}

AddTaxRegistrationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  existingTaxRegistrations: PropTypes.arrayOf(
    PropTypes.shape({
      taxableId: PropTypes.number.isRequired,
    })
  ).isRequired,
}
